'use server'

import { apiRequest, ApiResponse } from '../request'

export type WaitingListStatus =
    | 'WAITING'
    | 'NOTIFIED'
    | 'SCHEDULED'
    | 'CANCELLED'
    | 'EXPIRED'

export interface WaitingListEntry {
    id: number
    agendaId: number
    clientId: number
    orderId?: number
    priority: number
    preferredDates?: string
    preferredTimes?: string
    notes?: string
    status: WaitingListStatus
    notifiedAt?: string
    createdAt: string
    updatedAt: string
    client?: {
        id: number
        name: string
    }
    agenda?: {
        id: number
        professional?: {
            id: number
            name: string
        }
    }
}

export interface CreateWaitingListData {
    agendaId: number
    clientId: number
    orderId?: number
    priority?: number
    preferredDates?: string
    preferredTimes?: string
    notes?: string
}

export interface UpdateWaitingListData {
    priority?: number
    preferredDates?: string
    preferredTimes?: string
    notes?: string
    status?: WaitingListStatus
}

export interface ScheduleWaitingListData {
    eventTitle: string
    startDate: string
    endDate: string
    roomId?: number
}

export async function getWaitingList(): Promise<ApiResponse<WaitingListEntry[]>> {
    return apiRequest<WaitingListEntry[]>('waiting-list', {})
}

export async function getWaitingListEntry(
    id: number
): Promise<ApiResponse<WaitingListEntry>> {
    return apiRequest<WaitingListEntry>(`waiting-list/${id}`, {})
}

export async function getWaitingListByAgenda(
    agendaId: number
): Promise<ApiResponse<WaitingListEntry[]>> {
    return apiRequest<WaitingListEntry[]>(`waiting-list/agenda/${agendaId}`, {})
}

export async function getWaitingListByClient(
    clientId: number
): Promise<ApiResponse<WaitingListEntry[]>> {
    return apiRequest<WaitingListEntry[]>(`waiting-list/client/${clientId}`, {})
}

export async function createWaitingListEntry(
    data: CreateWaitingListData
): Promise<ApiResponse<WaitingListEntry>> {
    return apiRequest<WaitingListEntry>('waiting-list', { data }, 'post')
}

export async function updateWaitingListEntry(
    id: number,
    data: UpdateWaitingListData
): Promise<ApiResponse<WaitingListEntry>> {
    return apiRequest<WaitingListEntry>(`waiting-list/${id}`, { data }, 'patch')
}

export async function notifyWaitingListEntry(
    id: number
): Promise<ApiResponse<WaitingListEntry>> {
    return apiRequest<WaitingListEntry>(`waiting-list/${id}/notify`, {}, 'patch')
}

export async function scheduleWaitingListEntry(
    id: number,
    data: ScheduleWaitingListData
): Promise<ApiResponse<WaitingListEntry>> {
    return apiRequest<WaitingListEntry>(
        `waiting-list/${id}/schedule`,
        { data },
        'patch'
    )
}

export async function deleteWaitingListEntry(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`waiting-list/${id}`, {}, 'delete')
}

export const WAITING_LIST_STATUS_LABELS: Record<WaitingListStatus, string> = {
    WAITING: 'Aguardando',
    NOTIFIED: 'Notificado',
    SCHEDULED: 'Agendado',
    CANCELLED: 'Cancelado',
    EXPIRED: 'Expirado',
}

export const WAITING_LIST_STATUS_COLORS: Record<
    WaitingListStatus,
    { bg: string; text: string; border: string }
> = {
    WAITING: {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        border: 'border-amber-400',
    },
    NOTIFIED: {
        bg: 'bg-blue-100',
        text: 'text-blue-800',
        border: 'border-blue-400',
    },
    SCHEDULED: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-400',
    },
    CANCELLED: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-400',
    },
    EXPIRED: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        border: 'border-gray-400',
    },
}

export const PRIORITY_LABELS = [
    { value: 0, label: 'Baixa' },
    { value: 5, label: 'Normal' },
    { value: 10, label: 'Alta' },
]
